import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";
import type { FontFamily } from "@/lib/api";
import FontFaces, { fontStack } from "@/components/FontFaces";

const CATEGORY_RANK: Record<string, number> = { hand: 0, serif: 1, sans: 2, display: 3 };

function sortFonts(rows: FontFamily[]) {
  return [...rows].sort(
    (a, b) =>
      Number(Boolean(b.starred)) - Number(Boolean(a.starred)) ||
      Number(Boolean(b.available)) - Number(Boolean(a.available)) ||
      (CATEGORY_RANK[a.category || ""] ?? 9) - (CATEGORY_RANK[b.category || ""] ?? 9) ||
      a.name.localeCompare(b.name, "pl"),
  );
}

export default function FontPicker({
  fonts,
  value,
  onChange,
  starredOnly = false,
}: {
  fonts: FontFamily[];
  value?: string;
  onChange: (id: string) => void;
  /** W składzie karuzel: lista = gwiazdki (+ bieżący wybór). */
  starredOnly?: boolean;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const current =
    fonts.find((row) => row.id === value) || fonts.find((row) => row.id === "source-serif");
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = fonts;
    if (starredOnly && !showAll && !needle) {
      const starred = fonts.filter((row) => row.starred);
      const base = starred.length
        ? [...starred]
        : fonts.filter((row) => row.id === "source-serif" || row.id === "source-sans");
      const keep = new Set(base.map((row) => row.id));
      if (value && !keep.has(value)) {
        const extra = fonts.find((row) => row.id === value);
        if (extra) base.push(extra);
      }
      rows = base;
    }
    const filtered = rows.filter(
      (row) => !needle || row.name.toLowerCase().includes(needle) || row.id.includes(needle),
    );
    return sortFonts(filtered);
  }, [fonts, q, starredOnly, showAll, value]);
  const faces = current ? [current, ...shown.filter((row) => row.id !== current.id)] : shown;
  const starredCount = fonts.filter((row) => row.starred).length;

  return (
    <div className="relative min-w-[12rem]">
      <FontFaces fonts={faces} />
      <button
        type="button"
        className="flex h-8 w-full items-center justify-between rounded-md border border-input bg-transparent px-2 text-xs"
        style={{ fontFamily: fontStack(current) }}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="truncate">{current?.name || "Font"}</span>
        <span className="ml-2 text-[10px] text-muted-foreground">{current?.starred ? "★" : ""}</span>
      </button>
      {open ? (
        <div className="absolute z-30 mt-1 w-72 rounded-md border border-border bg-card p-2 shadow-lg">
          <input
            className="mb-2 h-8 w-full rounded-md border border-input bg-transparent px-2 text-xs"
            placeholder={starredOnly && !showAll ? "szukaj w gwiazdkach…" : "szukaj fontu…"}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
          {starredOnly ? (
            <div className="mb-2 flex flex-wrap items-center gap-2 px-1">
              <button
                type="button"
                className={cn(
                  "rounded px-1.5 py-0.5 text-[10px]",
                  !showAll ? "bg-brand/15 text-brand" : "text-muted-foreground hover:bg-accent/50",
                )}
                onClick={() => setShowAll(false)}
              >
                Gwiazdki ({starredCount})
              </button>
              <button
                type="button"
                className={cn(
                  "rounded px-1.5 py-0.5 text-[10px]",
                  showAll ? "bg-brand/15 text-brand" : "text-muted-foreground hover:bg-accent/50",
                )}
                onClick={() => setShowAll(true)}
              >
                Wszystkie
              </button>
            </div>
          ) : null}
          <div className="max-h-64 overflow-auto">
            {shown.map((row) => (
              <button
                key={row.id}
                type="button"
                className={cn(
                  "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs",
                  row.id === current?.id ? "bg-brand/10" : "hover:bg-accent/50",
                  !row.available ? "opacity-60" : "",
                )}
                style={{ fontFamily: fontStack(row) }}
                onClick={() => {
                  onChange(row.id);
                  setOpen(false);
                }}
              >
                <Star
                  className={cn(
                    "h-3 w-3 shrink-0",
                    row.starred ? "fill-current text-brand" : "text-muted-foreground",
                  )}
                />
                <span className="min-w-0 flex-1 truncate">{row.name}</span>
                <span className="shrink-0 text-[10px] text-muted-foreground">
                  {!row.available
                    ? "brak pliku"
                    : row.category === "hand"
                      ? "pisane"
                      : row.category === "display"
                        ? "ozdobne"
                        : ""}
                </span>
              </button>
            ))}
            {!shown.length ? (
              <p className="px-2 py-3 text-[11px] text-muted-foreground">
                Brak gwiazdek. Oznacz fonty na stronie Fonty.
              </p>
            ) : null}
          </div>
          <Link to="/fonty" className="mt-2 block px-2 text-[11px] text-brand underline">
            Fonty (gwiazdki)
          </Link>
        </div>
      ) : null}
    </div>
  );
}
